// @spec GHG-STR-091
/**
 * TanStack Query hooks for GHG facilities
 */

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateFacilityInput,
  Facility,
  FacilityListFilters,
  UpdateFacilityInput,
} from '../types';

import {
  createFacility,
  deleteFacility,
  fetchFacilities,
  fetchFacility,
  fetchFacilitySummary,
  updateFacility,
} from './facilities';
import { useGHGOrganizationId } from './use-ghg-organization-id';

/**
 * Query keys for facility queries
 */
export const facilityKeys = {
  all: ['ghg-facilities'] as const,
  lists: () => [...facilityKeys.all, 'list'] as const,
  list: (orgId: string, filters?: FacilityListFilters) =>
    [...facilityKeys.lists(), orgId, filters] as const,
  details: () => [...facilityKeys.all, 'detail'] as const,
  detail: (orgId: string, facilityId: string) =>
    [...facilityKeys.details(), orgId, facilityId] as const,
  summaries: () => [...facilityKeys.all, 'summary'] as const,
  summary: (orgId: string, facilityId: string) =>
    [...facilityKeys.summaries(), orgId, facilityId] as const,
};

/**
 * Hook to fetch facilities with filtering and pagination.
 */
export function useFacilities(filters?: FacilityListFilters) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: facilityKeys.list(organizationId ?? '', filters),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return fetchFacilities(getToken, organizationId, filters);
    },
    enabled: (isSignedIn ?? false) && !!organizationId,
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    facilities: query.data?.items ?? [],
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch a single facility by ID.
 */
export function useFacility(facilityId: string | undefined) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: facilityKeys.detail(organizationId ?? '', facilityId ?? ''),
    queryFn: async () => {
      if (!organizationId || !facilityId) {
        throw new Error('Organization or facility ID not found');
      }
      return fetchFacility(getToken, organizationId, facilityId);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!facilityId,
    staleTime: 60 * 1000, // 1 minute
  });

  return {
    facility: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch facility summary.
 */
export function useFacilitySummary(facilityId: string | undefined) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: facilityKeys.summary(organizationId ?? '', facilityId ?? ''),
    queryFn: async () => {
      if (!organizationId || !facilityId) {
        throw new Error('Organization or facility ID not found');
      }
      return fetchFacilitySummary(getToken, organizationId, facilityId);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!facilityId,
    staleTime: 60 * 1000,
  });

  return {
    summary: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to create a new facility.
 */
export function useCreateFacility() {
  const { getToken } = useAuth();
  const organizationId = useGHGOrganizationId();
  const queryClient = useQueryClient();

  return useMutation<Facility, Error, CreateFacilityInput>({
    mutationFn: async (data) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return createFacility(getToken, organizationId, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: facilityKeys.all });
    },
  });
}

/**
 * Hook to update an existing facility.
 */
export function useUpdateFacility() {
  const { getToken } = useAuth();
  const organizationId = useGHGOrganizationId();
  const queryClient = useQueryClient();

  return useMutation<Facility, Error, { facilityId: string; data: UpdateFacilityInput }>({
    mutationFn: async ({ facilityId, data }) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return updateFacility(getToken, organizationId, facilityId, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: facilityKeys.all });
    },
  });
}

/**
 * Hook to delete a facility.
 */
export function useDeleteFacility() {
  const { getToken } = useAuth();
  const organizationId = useGHGOrganizationId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (facilityId: string) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      await deleteFacility(getToken, organizationId, facilityId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: facilityKeys.all });
    },
  });
}
